import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile, listMyOrders, claimMyOrders } from "@/lib/account/account.functions";
import { Package, MapPin, User, ArrowRight, Truck, Clock, XCircle, RotateCcw, ReceiptText, CreditCard } from "lucide-react";
import { HistoryRail } from "@/components/product/HistoryRail";
import { useEffect } from "react";

export const Route = createFileRoute("/_account/account/")({
  component: AccountHome,
});

const statusMeta: Record<string, { label: string; icon: any; tone: string }> = {
  pending_payment: { label: "Pending payment", icon: Clock, tone: "bg-slate-100 text-slate-700" },
  paid: { label: "Paid", icon: CreditCard, tone: "bg-sky-100 text-sky-900" },
  started: { label: "In production", icon: Package, tone: "bg-amber-100 text-amber-900" },
  shipped: { label: "Shipped", icon: Truck, tone: "bg-emerald-100 text-emerald-900" },
  cancelled: { label: "Cancelled", icon: XCircle, tone: "bg-zinc-100 text-zinc-700" },
  returned: { label: "Returned", icon: RotateCcw, tone: "bg-rose-100 text-rose-900" },
};

function metaFor(status: string) {
  return (
    statusMeta[status] ?? {
      label: String(status ?? "").replace(/_/g, " ") || "Unknown",
      icon: Clock,
      tone: "bg-slate-100 text-slate-700",
    }
  );
}


function AccountHome() {
  const getProfile = useServerFn(getMyProfile);
  const listOrders = useServerFn(listMyOrders);
  const claim = useServerFn(claimMyOrders);

  const profileQ = useQuery({ queryKey: ["account", "profile"], queryFn: () => getProfile() });
  const ordersQ = useQuery({ queryKey: ["account", "orders", "recent"], queryFn: () => listOrders() });

  // try claim on first mount
  useEffect(() => {
    claim().catch(() => {});
  }, []);

  const orders = ordersQ.data ?? [];
  const profile = profileQ.data?.profile;
  const greeting = greet(profile?.first_name);

  const stats = {
    total: orders.length,
    inFlight: orders.filter((o: any) => o.status === "paid" || o.status === "started").length,
    shipped: orders.filter((o: any) => o.status === "shipped").length,
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#172554] to-[#2563EB] p-6 text-white shadow-sm">
        <p className="text-sm text-white/70">{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">{greeting}</h1>
        <p className="mt-2 max-w-xl text-sm text-white/80">
          Track every order from payment to your doorstep, manage saved addresses, and reorder OEM-matched parts in two clicks.
        </p>
        <div className="mt-5 grid grid-cols-3 gap-3 sm:max-w-md">
          <Stat label="Total" value={stats.total} />
          <Stat label="In progress" value={stats.inFlight} />
          <Stat label="Shipped" value={stats.shipped} />
        </div>
      </div>

      {/* Quick links */}
      <div className="grid gap-3 sm:grid-cols-3">
        <QuickLink to="/account/orders" icon={Package} title="Your orders" desc="Track and reorder" />
        <QuickLink to="/account/addresses" icon={MapPin} title="Addresses" desc="Save delivery details" />
        <QuickLink to="/account/billing" icon={ReceiptText} title="Billing details" desc="Invoices & VAT setup" />
        <QuickLink to="/account/profile" icon={User} title="Profile" desc="Name, phone, prefs" />
      </div>

      {/* Recent orders */}
      <section className="rounded-2xl border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-100 p-5">
          <h2 className="text-lg font-bold text-slate-900">Recent orders</h2>
          <Link to="/account/orders" className="text-sm font-medium text-[#2563EB] hover:underline">
            View all →
          </Link>
        </header>
        {ordersQ.isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : orders.length === 0 ? (
          <EmptyOrders />
        ) : (
          <ul className="divide-y divide-slate-100">
            {orders.slice(0, 5).map((o: any) => {
              const m = metaFor(o.status);
              const Icon = m.icon;
              const items = (o.order_items ?? []).reduce((s: number, i: any) => s + i.quantity, 0);
              return (
                <li key={o.id}>
                  <Link
                    to={"/account/orders/$id" as never}
                    params={{ id: o.id } as never}
                    className="flex items-center gap-4 p-4 transition hover:bg-slate-50"
                  >
                    <div className={`grid h-10 w-10 place-items-center rounded-full ${m.tone}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">#{o.order_number}</p>
                      <p className="text-xs text-slate-500">
                        {items} item{items === 1 ? "" : "s"} · {o.country}
                        {o.tracking_number ? ` · ${o.tracking_carrier} ${o.tracking_number}` : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{Number(o.total_eur).toFixed(2)} €</p>
                      <p className="text-xs text-slate-500">{m.label}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <HistoryRail />
    </div>
  );
}


function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-white/10 px-3 py-2 ring-1 ring-white/15">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-white/70">{label}</p>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, desc }: any) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-[#2563EB] hover:shadow-md"
    >
      <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100 group-hover:bg-[#2563EB] group-hover:text-white transition">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{desc}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#2563EB]" />
    </Link>
  );
}

function EmptyOrders() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-slate-100">
        <Package className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="mt-3 text-sm font-semibold text-slate-900">No orders yet</h3>
      <p className="mt-1 text-sm text-slate-500">Browse parts and your orders will appear here.</p>
      <Link
        to="/shop"
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1d4ed8]"
      >
        Browse parts <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function greet(name?: string | null) {
  const h = new Date().getHours();
  const part = h < 5 ? "Hello" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  return name ? `${part}, ${name}` : `${part}`;
}
